import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import Seo from "@/components/feature/Seo";
import { editors, stats, testimonials } from "@/mocks/site";

const principles = [
  { n: "01", t: "观点清晰，不故作高深", d: "把复杂的事讲清楚，才是真本事。看料要求每篇内容都有一个读者能复述的观点。" },
  { n: "02", t: "来源可靠，拒绝道听途说", d: "涉及事实与数据的内容，编辑需多方核对。宁缺毋滥，不为流量牺牲准确。" },
  { n: "03", t: "尊重时间，拒绝注水", d: "能三句话说清的，绝不用三段绕。排版克制、重点突出，尊重每一位读者的时间。" },
  { n: "04", t: "实事求是，立场中立", d: "不做夸大其词的标题，不迎合极端情绪，用平实的语气呈现真实的判断。" },
];

const journey = [
  { y: "筛选", t: "从海量信息中锁定主题", d: "结合读者关注与趋势，找到「今天真正值得聊」的选题。" },
  { y: "甄别", t: "逐条核实与取舍", d: "对信源与数据逐一核查，剔除片面与夸张，只留经得起推敲的部分。" },
  { y: "打磨", t: "用舒服的节奏写成文", d: "让文字易于进入、信息密度恰到好处，读完有收获、有行动。" },
];

export default function AboutPage() {
  return (
    <PageLayout dark={false}>
      <Seo
        title="关于看料 - 内容定位、发展理念与编辑团队"
        description="了解看料：一个以编辑甄选为核心的内容推荐平台。我们坚持观点清晰、来源可靠、尊重时间的内容原则，由专业编辑团队每日为读者甄选值得看的好料。"
        keywords="关于看料,看料内容定位,看料编辑团队,看料内容原则,内容推荐平台"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于看料",
            url: "https://01x0wyo.cn/about",
            inLanguage: "zh-CN",
            description: "看料是一个以编辑甄选为核心的内容推荐平台。",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://01x0wyo.cn/" },
              { "@type": "ListItem", position: 2, name: "关于看料", item: "https://01x0wyo.cn/about" },
            ],
          },
        ]}
      />

      <section className="bg-background-50 pt-24 md:pt-28">
        <div className="mx-auto max-w-7xl px-4 pb-6 md:px-6">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">关于看料</span>
          </nav>

          <div className="relative mt-8 overflow-hidden rounded-2xl bg-background-100 px-6 py-12 md:px-12 md:py-16">
            <div aria-hidden className="pointer-events-none absolute -right-10 -top-14 h-64 w-64 rounded-full bg-accent-100 blur-2xl" />
            <span className="inline-flex items-center gap-2 rounded-md bg-primary-100 px-2.5 py-1 text-xs font-semibold text-primary-800">
              <i className="ri-information-line" />
              关于我们
            </span>
            <h1 className="relative mt-5 max-w-2xl font-heading text-3xl font-black leading-tight text-foreground-950 md:text-5xl md:leading-[1.2]">
              我们相信，好的内容
              <br />
              值得被认真挑出来
            </h1>
            <p className="relative mt-5 max-w-2xl text-base leading-8 text-foreground-700">
              看料是一个以「编辑甄选」为核心的内容推荐平台。我们相信，在信息过载的今天，
              最有价值的不是无限的内容供给，而是帮你筛选出一份「今天真正值得看」的清单。
            </p>
            <div className="relative mt-8 grid grid-cols-2 gap-6 lg:grid-cols-4">
              {stats.map((s) => (
                <div key={s.label}>
                  <p className="font-heading text-2xl font-black text-primary-600 md:text-3xl">{s.value}</p>
                  <p className="mt-1 text-xs text-foreground-500 md:text-sm">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 定位 */}
      <section className="bg-background-50">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="grid gap-8 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-semibold text-secondary-900">
                <i className="ri-compass-3-line" />
                内容定位
              </span>
              <h2 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                看料替你看好料，把最好的精力留给最有价值的阅读
              </h2>
            </div>
            <div className="space-y-5 text-[17px] leading-8 text-foreground-700 lg:col-span-7">
              <p>
                看料的内容聚焦四个彼此呼应、又相互交叉的方向——<strong>数字工作</strong>关注效率与方法，
                <strong>阅读新知</strong>关注输入与思考，<strong>生活灵感</strong>关注日常的秩序与情绪，
                <strong>认知成长</strong>关注自我管理与长期进步。它们共同回答一个问题：如何把日子过得更有掌控感、更有温度。
              </p>
              <p>
                我们不做搬运式的内容聚合。每一篇「看料精选」都经过编辑的重新梳理、核实与表达，
                让它拥有独立的观点，也经得起细读。读者不必在几十个标签页之间来回奔波，
                打开看料，一份经过筛选的好料清单就在眼前。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 发展理念 */}
      <section className="bg-background-100 border-y border-background-200/70">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-2 rounded-md bg-accent-100 px-2.5 py-1 text-xs font-semibold text-accent-900">
              <i className="ri-compasses-2-line" />
              我们如何工作
            </span>
            <h2 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              一份好料诞生的三个步骤
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {journey.map((step, i) => (
              <div key={step.y} className="relative rounded-xl border border-background-200/80 bg-background-50 p-6">
                <span className="font-heading text-xs font-bold tracking-[0.2em] text-primary-500">STEP 0{i + 1}</span>
                <h3 className="mt-3 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500 text-lg text-background-50">
                    <i className={i === 0 ? "ri-focus-3-line" : i === 1 ? "ri-check-double-line" : "ri-quill-pen-line"} />
                  </span>
                  {step.t}
                </h3>
                <p className="mt-3 text-sm leading-7 text-foreground-600">{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 内容原则 */}
      <section id="values" className="scroll-mt-24 bg-background-50">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-2 rounded-md bg-primary-100 px-2.5 py-1 text-xs font-semibold text-primary-800">
                <i className="ri-scales-3-line" />
                内容原则
              </span>
              <h2 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                四条底线，写进每一篇内容里
              </h2>
            </div>
            <span className="text-sm text-foreground-500">不因流量妥协，不以噱头取胜</span>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {principles.map((p) => (
              <div
                key={p.n}
                className="group flex gap-5 rounded-xl border border-background-200/80 bg-background-100 p-6 transition-colors hover:border-primary-200"
              >
                <span className="font-heading text-3xl font-black text-primary-500/60 transition-colors group-hover:text-primary-500">
                  {p.n}
                </span>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-950">{p.t}</h3>
                  <p className="mt-2 text-sm leading-7 text-foreground-600">{p.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 编辑团队 */}
      <section id="team" className="scroll-mt-24 bg-background-100 border-y border-background-200/70">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-2 rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-semibold text-secondary-900">
              <i className="ri-team-line" />
              编辑团队
            </span>
            <h2 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              认识为你选好料的人
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-600">
              他们来自不同领域，但共享同一份挑剔：只把自己愿意花时间读完的内容推荐给你。
            </p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {editors.map((e) => (
              <div
                key={e.name}
                className="flex flex-col items-center rounded-xl border border-background-200/80 bg-background-50 p-6 text-center transition-colors hover:border-primary-200"
              >
                <span className={`flex h-16 w-16 items-center justify-center rounded-full font-heading text-2xl font-bold text-background-50 ${e.color}`}>
                  {e.avatar}
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{e.name}</h3>
                <p className="mt-1 text-xs font-semibold text-primary-600">{e.role}</p>
                <p className="mt-3 text-sm leading-6 text-foreground-600">{e.intro}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 口碑 + 联系 */}
      <section className="bg-background-50">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="grid gap-8 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h2 className="font-heading text-2xl font-black text-foreground-950">读者怎么说</h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                {testimonials.map((t) => (
                  <figure key={t.name} className="rounded-xl border border-background-200/80 bg-background-100 p-5">
                    <div className="flex gap-0.5 text-sm text-accent-500">
                      {[0, 1, 2, 3, 4].map((s) => <i key={s} className="ri-star-fill" />)}
                    </div>
                    <blockquote className="mt-3 text-sm leading-6 text-foreground-700">「{t.quote}」</blockquote>
                    <figcaption className="mt-4 text-xs font-semibold text-foreground-900">
                      {t.name} · {t.meta}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl bg-foreground-950 p-7 md:p-8 text-background-50">
                <h2 className="flex items-center gap-2 font-heading text-2xl font-black">
                  <i className="ri-mail-send-line text-accent-500" />
                  联系我们
                </h2>
                <p className="mt-3 text-sm leading-7 text-background-50/75">
                  有选题想推荐、有内容想投稿，或对我们的编辑工作有任何建议，欢迎来信。
                </p>
                <a
                  href="mailto:hello@kanliao.example"
                  className="mt-6 inline-flex h-11 items-center gap-2 rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-400 whitespace-nowrap"
                >
                  <i className="ri-mail-line" />
                  hello@kanliao.example
                </a>
                <div className="mt-8 grid gap-3 border-t border-background-50/15 pt-5 text-sm">
                  <p className="flex items-center gap-2 text-background-50/85">
                    <i className="ri-time-line text-accent-400" /> 工作日 9:00–18:00 回复
                  </p>
                  <Link to="/faq" className="inline-flex items-center gap-2 text-background-50/85 transition-colors hover:text-accent-300">
                    <i className="ri-question-answer-line text-accent-400" /> 常见问题（点击查看）
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}